import React from 'react';
import PropTypes from 'prop-types';
import { cn, getInitialNames } from '@/lib/utils/helpers';
import { Avatar, AvatarFallback, AvatarImage } from '@/lib/shadcn/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/shadcn/ui/tooltip';
import CommentStatus from './CommentStatus';
import { Reply, Trash2 } from 'lucide-react';
import { formatRelativeDate } from '@/lib/utils/helpers';

/**
 * CommentItem component for displaying individual comments
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes to apply
 * @param {string} props.id - Comment ID
 * @param {string} props.author - Comment author name
 * @param {string} props.content - Comment content
 * @param {string} props.createdAt - Comment date
 * @param {boolean} props.isReply - Whether this comment is a reply to another comment
 * @param {Function} props.onReply - Callback function when reply button is clicked
 * @param {Object} props.user - User object containing avatar information
 * @returns {JSX.Element} CommentItem component
 */
export default function CommentItem({
  className,
  user,
  id,
  author,
  authorAvatar,
  content,
  createdAt,
  isReply = false,
  onReply,
  onDelete,
  status,
  ownerId // The user ID of the comment author
}) {
  const handleReplyClick = () => {
    if (onReply) {
      onReply(id);
    }
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(id);
    }
  };

  return (
      <div className={cn(
        'mb-8',
        isReply && 'mom-border ml-4 border-l-2 pl-5',
        className
      )}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-x-2 gap-y-0 flex-wrap">
            <Avatar className="h-8 w-8 rounded-full border-2 border-navy dark:border-silver">
              <AvatarImage src={authorAvatar} alt={author} />
              <AvatarFallback className="rounded-full text-sidebar-foreground">{getInitialNames(author)}</AvatarFallback>
            </Avatar>

            <span className={cn('text-h5 font-serif mb-0', { 'text-h6': isReply })}>
              {author}
            </span>

            <span className={cn('text-sm text-navy dark:text-silver')}>{isReply ? 'replied' : 'commented'}</span>
          </div>

          {status && status !== 'approved' ? (
            <CommentStatus
              status={status}
              createdAt={createdAt}
            />
          ) : (
            <span className="text-sm whitespace-nowrap text-gray-600 dark:text-gray-500" suppressHydrationWarning>
              {formatRelativeDate(createdAt)}
            </span>
          )}
        </div>

        <div className="prose prose-sm dark:prose-invert max-w-none mb-3">
          <p>{content}</p>
        </div>

        {(user) && (
          <div className="flex gap-4">
            {(onReply && !isReply) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleReplyClick}
                    className="p-1 text-gray-500 hover:text-navy dark:hover:text-silver transition-colors"
                    aria-label="Reply"
                  >
                    <Reply className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  Reply to this comment
                </TooltipContent>
              </Tooltip>
            )}

            {onDelete && user?.id === ownerId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleDeleteClick}
                    className="p-1 text-gray-500 hover:text-red transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  Delete this comment
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>
  );
}

CommentItem.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  author: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  isReply: PropTypes.bool,
  onReply: PropTypes.func,
  onDelete: PropTypes.func,
  user: PropTypes.object,
  ownerId: PropTypes.string,
  status: PropTypes.oneOf(['pending', 'approved', 'declined'])
};
